import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { exchangeRatesApi } from '../services/api/endpoints/exchangeRates';
import { useCurrencyStore } from '../stores/currencyStore';
import { CurrencyCode, CURRENCIES } from '../config/currency';
import { roundMoney } from '../utils/currency';
import type { 
  RevaluationRequest, 
  RevaluationSummary 
} from '../types/settings';

/**
 * Hook pour la gestion complète des taux de change
 * Intègre le store local et l'API backend
 */
export function useExchangeRates() {
  const queryClient = useQueryClient();
  const {
    baseCurrency,
    displayCurrency,
    exchangeRates,
    lastRatesUpdate,
    isLoading: storeLoading,
    setRatesFromRecord,
    setLoading,
    getRate,
    hasValidRate,
    toBaseCurrency,
    fromBaseCurrency,
    convert,
    convertWithDetails
  } = useCurrencyStore();

  // ============================================
  // QUERIES
  // ============================================

  // Récupérer les taux actuels depuis l'API
  const {
    data: serverRates,
    isLoading: ratesLoading,
    error: ratesError,
    refetch: refetchRates
  } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: async () => {
      const response = await exchangeRatesApi.getCurrentRates();
      if (response.success && response.data) {
        // Synchroniser avec le store local
        setRatesFromRecord(response.data as Record<string, number>);
        return response.data;
      }
      throw new Error('Échec de récupération des taux');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });

  // Récupérer les devises supportées
  const {
    data: supportedCurrencies,
    isLoading: currenciesLoading
  } = useQuery({
    queryKey: ['supported-currencies'],
    queryFn: async () => {
      const response = await exchangeRatesApi.getSupportedCurrencies();
      if (response.success && response.data) {
        return response.data;
      }
      // Fallback sur les devises locales
      return Object.values(CURRENCIES).map(c => ({
        code: c.code,
        name: c.name,
        symbol: c.symbol,
        isDefault: c.default,
        decimals: c.decimals
      }));
    },
    staleTime: 30 * 60 * 1000 // 30 minutes
  });

  // ============================================
  // MUTATIONS
  // ============================================

  // Mettre à jour les taux de change
  const updateRatesMutation = useMutation({
    mutationFn: async (rates: Array<{ currencyCode: string; exchangeRate: number }>) => {
      const response = await exchangeRatesApi.updateRates({ rates });
      if (!response.success) {
        throw new Error('Échec de mise à jour des taux');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
    }
  });

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Calculer le montant en CDF depuis une devise étrangère
   */
  const calculateCDFAmount = useCallback((
    amount: number,
    currency: CurrencyCode,
    customRate?: number
  ): number => {
    if (currency === 'CDF') return amount;
    
    const rate = customRate ?? getRate(currency);
    if (!rate || rate <= 0) {
      console.warn(`Taux invalide pour ${currency}, retourne le montant original`);
      return amount;
    }
    
    return roundMoney(amount * rate);
  }, [getRate]);

  /**
   * Calculer le montant en devise étrangère depuis CDF
   */
  const calculateForeignAmount = useCallback((
    amountCDF: number,
    targetCurrency: CurrencyCode,
    customRate?: number
  ): number => {
    if (targetCurrency === 'CDF') return amountCDF;
    
    const rate = customRate ?? getRate(targetCurrency);
    if (!rate || rate <= 0) {
      console.warn(`Taux invalide pour ${targetCurrency}, retourne le montant original`);
      return amountCDF;
    }
    
    return roundMoney(amountCDF / rate);
  }, [getRate]);

  /**
   * Formater un montant avec devise et conversion optionnelle
   */
  const formatWithConversion = useCallback((
    amount: number,
    originalCurrency: CurrencyCode,
    showOriginal: boolean = true
  ): string => {
    const locale = displayCurrency === 'USD' ? 'en-US' : 'fr-CD';
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    if (originalCurrency === displayCurrency) {
      return formatter.format(amount);
    }
    
    const convertedAmount = convert(amount, originalCurrency, displayCurrency);
    const converted = formatter.format(convertedAmount);
    
    if (showOriginal) {
      const originalFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: originalCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return `${converted} (${originalFormatter.format(amount)})`;
    }
    
    return converted;
  }, [displayCurrency, convert]);

  /**
   * Obtenir le taux suggéré pour une devise (depuis API ou store)
   */
  const getSuggestedRate = useCallback((currency: CurrencyCode): number => {
    // Priorité aux taux du serveur, sinon store local
    if (serverRates && currency in serverRates) {
      return (serverRates as Record<string, number>)[currency];
    }
    return getRate(currency);
  }, [serverRates, getRate]);

  /**
   * Vérifier si une devise est supportée
   */
  const isCurrencySupported = useCallback((code: string): boolean => {
    return code in CURRENCIES;
  }, []);

  return {
    // État
    baseCurrency,
    displayCurrency,
    exchangeRates,
    lastRatesUpdate,
    isLoading: ratesLoading || storeLoading,
    error: ratesError,
    supportedCurrencies: supportedCurrencies || Object.values(CURRENCIES),
    
    // Actions
    refetchRates,
    updateRates: updateRatesMutation.mutateAsync,
    isUpdating: updateRatesMutation.isPending,
    
    // Helpers
    getRate,
    getSuggestedRate,
    hasValidRate,
    calculateCDFAmount,
    calculateForeignAmount,
    toBaseCurrency,
    fromBaseCurrency,
    convert,
    convertWithDetails,
    formatWithConversion,
    isCurrencySupported
  };
}

/**
 * Hook pour l'historique des taux de change
 */
export function useExchangeRateHistory(params?: {
  fromCurrency?: string;
  toCurrency?: string;
  startDate?: string;
  endDate?: string;
}) {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['exchange-rate-history', params],
    queryFn: async () => {
      const response = await exchangeRatesApi.getHistory(params);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Échec de récupération de l\'historique');
    },
    enabled: !!params // Ne charge que si des params sont fournis
  });

  return {
    history: data?.data || [],
    total: data?.total || 0,
    isLoading,
    error,
    refetch
  };
}

/**
 * Hook pour la réévaluation des devises
 */
export function useRevaluation() {
  const queryClient = useQueryClient();
  const [lastResult, setLastResult] = useState<RevaluationSummary | null>(null);

  // Lancer une réévaluation
  const runRevaluationMutation = useMutation({
    mutationFn: async (request: RevaluationRequest) => {
      const response = await exchangeRatesApi.runRevaluation(request);
      if (!response.success || !response.data) {
        throw new Error('Échec de la réévaluation');
      }
      return response.data;
    },
    onSuccess: (data) => {
      setLastResult(data);
      // Invalider les requêtes liées aux écritures et rapports
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });

  // Historique des réévaluations
  const {
    data: historyData,
    isLoading: historyLoading
  } = useQuery({
    queryKey: ['revaluation-history'],
    queryFn: async () => {
      const response = await exchangeRatesApi.getRevaluationHistory();
      if (response.success && response.data) {
        return response.data;
      }
      return { data: [], total: 0 };
    }
  });

  return {
    runRevaluation: runRevaluationMutation.mutateAsync,
    isRunning: runRevaluationMutation.isPending,
    lastResult,
    history: historyData?.data || [],
    historyLoading,
    error: runRevaluationMutation.error
  };
}

/**
 * Hook pour l'analyse du risque de change
 */
export function useCurrencyRiskExposure(fiscalYearId?: string) {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['currency-risk', fiscalYearId],
    queryFn: async () => {
      if (!fiscalYearId) throw new Error('Exercice fiscal requis');
      
      const response = await exchangeRatesApi.getRiskExposure({ fiscalYearId });
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Échec de l\'analyse du risque');
    },
    enabled: !!fiscalYearId
  });

  return {
    exposures: data?.exposures || [],
    totalRiskValue: data?.totalRiskValue || 0,
    baseCurrency: data?.baseCurrency || 'CDF',
    asOfDate: data?.asOfDate,
    isLoading,
    error,
    refetch
  };
}
